import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { C, FONTS } from './tokens/design';
import { useAuth } from './context/AuthContext';
import { useTheme } from './context/ThemeContext';
import { UserProvider, useUser } from './context/UserContext';
import { useUpdateBranding, clearBrandingCache } from './context/BrandingContext';
import { fetchBranding } from './auth/brandingClient';
import { useClient } from './context/ClientContext';
import { useApiCall } from './hooks/useApi';
import { useApiClient } from './api/apiClient';
import Sidebar from './components/layout/Sidebar';
import ErrorBoundary from './components/ErrorBoundary';
import { FindingsProvider } from './context/FindingsContext';
import { AlertProvider } from './context/AlertContext';
import AlertBanner from './components/AlertBanner';
import { ToastProvider } from './components/ui/Toast';
import { ImpersonationProvider, useImpersonation } from './context/ImpersonationContext';
import { ViewModeProvider } from './context/ViewModeContext';
// Auth pages (public)
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import AcceptInvite from './pages/auth/AcceptInvite';
import MfaSetup from './pages/auth/MfaSetup';
import SsoCallback from './pages/auth/SsoCallback';

// Client pages
import ClientDashboard from './pages/client/Dashboard';
import FindingsList from './pages/client/FindingsList';
import FindingDetail from './pages/client/FindingDetail';
import Assets from './pages/client/Assets';
import AssetDetail from './pages/client/AssetDetail';
import ScanHistory from './pages/client/ScanHistory';
import ScanSchedule from './pages/client/ScanSchedule';
import ClientSecrets from './pages/client/ClientSecrets';
import ClientCredentials from './pages/client/Credentials';

// MSP pages
import MspDashboard from './pages/msp/Dashboard';
import MspClients from './pages/msp/Clients';
import MspAlerts from './pages/msp/Alerts';
import MspReports from './pages/msp/Reports';
import MspSettings from './pages/msp/Settings';
import MspTeam from './pages/msp/Team';
import OnboardingWizard from './pages/msp/OnboardingWizard';

// Platform pages
import PlatformDashboard from './pages/platform/Dashboard';
import PlatformMspDetail from './pages/platform/MspDetail';
import WhiteLabelVerify from './pages/platform/WhiteLabelVerify';
import PlatformSettings from './pages/platform/Settings';

// Account pages
import Profile from './pages/account/Profile';
import AccountSettings from './pages/account/AccountSettings';
import AlertPreferences from './pages/account/AlertPreferences';
import TeamMembers from './pages/account/TeamMembers';
import ApiKeys from './pages/account/ApiKeys';
import AuditLog from './pages/account/AuditLog';
import Shell from './components/layout/Shell';
import Card from './components/ui/Card';
import Button from './components/ui/Button';
import PageError from './components/ui/PageError';
import { currentApiInflight, markRouteTiming } from './utils/performanceDiagnostics';

const Osint = lazy(() => import('./pages/client/Osint'));
const SensitiveData = lazy(() => import('./pages/client/SensitiveData'));
const Compliance = lazy(() => import('./pages/client/Compliance'));
const ComplianceEvidence = lazy(() => import('./pages/client/ComplianceEvidence'));
const AttackPaths = lazy(() => import('./pages/client/AttackPaths'));
const AttackPathDetail = lazy(() => import('./pages/client/AttackPathDetail'));
const Reports = lazy(() => import('./pages/client/Reports'));
const CloudSecurity = lazy(() => import('./pages/client/CloudSecurity'));
const DastScanning = lazy(() => import('./pages/client/DastScanning'));
const ADSecurity = lazy(() => import('./pages/client/ADSecurity'));

function RouteLoadingFallback({ label = 'Loading page' }) {
  return (
    <main style={{ flex: 1, padding: 24 }}>
      <Card style={{ padding: 18 }}>
        <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono }}>{label}...</div>
      </Card>
    </main>
  );
}

function LazyClientPage({ children, label }) {
  return (
    <ClientPage>
      <Suspense fallback={<RouteLoadingFallback label={label} />}>
        {children}
      </Suspense>
    </ClientPage>
  );
}

function clientRecordId(client) {
  return client?.clientId ?? client?.id ?? '';
}

function clientRecordName(client) {
  return client?.name ?? client?.companyName ?? client?.displayName ?? clientRecordId(client);
}

export function clientBelongsToActiveMsp(client, activeMspId) {
  return !activeMspId || String(client?.mspId ?? '').toLowerCase() === String(activeMspId).toLowerCase();
}

function toClientArray(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    for (const key of ['items', 'results', 'data', 'clients']) {
      if (Array.isArray(data[key])) return data[key];
    }
  }
  return [];
}

function ActiveClientRequired({ clients, loading, error, staleClientId, onSelect, onReturn }) {
  const [selectedClientId, setSelectedClientId] = useState('');

  useEffect(() => {
    if (!selectedClientId && clients.length > 0) {
      setSelectedClientId(clientRecordId(clients[0]));
    }
  }, [clients, selectedClientId]);

  const selectedClient = clients.find(client => clientRecordId(client) === selectedClientId);

  return (
    <Shell
      title="Active Client Required"
      sub="Select and verify a client before opening client-scoped pages."
    >
      {error && <PageError error={error} />}
      <Card style={{ padding: 22, maxWidth: 760 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: C.t1, fontFamily: FONTS.sans, marginBottom: 8 }}>
          Verify active client
        </div>
        <div style={{ fontSize: 12, color: C.t2, lineHeight: 1.6, maxWidth: 640, marginBottom: 16 }}>
          Platform and MSP users must select a current client before viewing client findings, assets, scans, reports, or settings. This prevents client pages from using an old browser session value.
        </div>
        {staleClientId && (
          <div style={{ padding: '8px 10px', borderRadius: 6, background: `${C.amb}12`, border: `1px solid ${C.amb}33`, color: C.amb, fontSize: 10, fontFamily: FONTS.mono, marginBottom: 14 }}>
            Cleared unavailable activeClientId: {staleClientId}
          </div>
        )}
        {loading ? (
          <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono }}>Loading available clients...</div>
        ) : clients.length > 0 ? (
          <div style={{ display: 'grid', gap: 12 }}>
            <select
              value={selectedClientId}
              onChange={event => setSelectedClientId(event.target.value)}
              style={{
                width: '100%',
                maxWidth: 460,
                background: C.s2,
                border: `1px solid ${C.b1}`,
                borderRadius: 6,
                padding: '8px 10px',
                color: C.t1,
                fontSize: 12,
                fontFamily: FONTS.mono,
              }}
            >
              {clients.map(client => {
                const id = clientRecordId(client);
                return <option key={id} value={id}>{clientRecordName(client)}</option>;
              })}
            </select>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button variant="pri" disabled={!selectedClient} onClick={() => selectedClient && onSelect(selectedClient)}>
                Set Active Client
              </Button>
              <Button variant="sec" onClick={onReturn}>
                Return to Dashboard
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono }}>
              No available clients were returned for this account.
            </div>
            <Button variant="sec" onClick={onReturn}>Return to Dashboard</Button>
          </div>
        )}
      </Card>
    </Shell>
  );
}

function ClientRouteGuard({ children }) {
  const navigate = useNavigate();
  const { isPlatformAdmin, isMspUser, isClientUser, profile } = useUser();
  const { clientId, setClientId, clientName, setClientName } = useClient();
  const { impersonatedMsp } = useImpersonation();
  const activeMspId = impersonatedMsp?.mspId ?? null;
  const api = useApiClient();
  const needsVerifiedClient = isPlatformAdmin === true || isMspUser === true;
  const clientsCall = useApiCall(needsVerifiedClient ? '/clients' : null);
  const clients = useMemo(() => toClientArray(clientsCall.data), [clientsCall.data]);
  const [platformProfile, setPlatformProfile] = useState(null);

  useEffect(() => {
    if (isPlatformAdmin !== true || !clientId) return;
    let cancelled = false;
    api.get(`/clients/${clientId}/profile`)
      .then(record => {
        if (!cancelled) setPlatformProfile({ requestedClientId: clientId, requestedMspId: activeMspId, record });
      })
      .catch(error => {
        if (!cancelled) setPlatformProfile({ requestedClientId: clientId, requestedMspId: activeMspId, error });
      });
    return () => { cancelled = true; };
  }, [api, isPlatformAdmin, clientId, activeMspId]);

  // The MSP client list is intentionally scoped. Platform admins verify a selected
  // client through its authorized profile endpoint before opening client pages.
  const profilePending = isPlatformAdmin === true && !!clientId &&
    (platformProfile?.requestedClientId !== clientId || platformProfile?.requestedMspId !== activeMspId);
  const loading = needsVerifiedClient && (clientsCall.loading || profilePending);
  const error = needsVerifiedClient ? clientsCall.error : null;
  const [staleClientId, setStaleClientId] = useState('');

  useEffect(() => {
    if (isClientUser === true && profile?.clientId && clientId !== profile.clientId) {
      setClientId(profile.clientId);
      setClientName(profile.clientName ?? profile.companyName ?? profile.name ?? null);
    }
  }, [isClientUser, profile, clientId, setClientId, setClientName]);

  const activeClient = useMemo(() => {
    if (!clientId) return null;
    if (isPlatformAdmin === true) {
      // Platform mode permits cross-MSP selection; an active MSP view requires that MSP's client.
      const verified = platformProfile?.requestedClientId === clientId &&
        platformProfile?.requestedMspId === activeMspId ? platformProfile.record : null;
      if (!verified || String(clientRecordId(verified)).toLowerCase() !== String(clientId).toLowerCase()) return null;
      if (!clientBelongsToActiveMsp(verified, activeMspId)) return null;
      return verified;
    }
    return clients.find(client => String(clientRecordId(client)).toLowerCase() === String(clientId).toLowerCase()) ?? null;
  }, [clients, clientId, platformProfile, isPlatformAdmin, activeMspId]);

  useEffect(() => {
    if (!needsVerifiedClient || loading || !clientId) return;
    if (activeClient) {
      const verifiedName = clientRecordName(activeClient);
      if (verifiedName && verifiedName !== clientName) setClientName(verifiedName);
      setStaleClientId('');
      return;
    }
    setStaleClientId(clientId);
    setClientId(null);
  }, [needsVerifiedClient, loading, clientId, activeClient, clientName, setClientId, setClientName]);

  if (!needsVerifiedClient) return children;
  if (loading && clientId) {
    return (
      <Shell title="Verifying Active Client" sub="Checking the active client before loading client data.">
        <Card style={{ padding: 18 }}>
          <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono }}>Verifying active client...</div>
        </Card>
      </Shell>
    );
  }
  if (!clientId || !activeClient) {
    return (
      <ActiveClientRequired
        clients={clients}
        loading={loading}
        error={error}
        staleClientId={staleClientId}
        onSelect={(client) => {
          setClientId(clientRecordId(client));
          setClientName(clientRecordName(client));
        }}
        onReturn={() => navigate(isPlatformAdmin ? '/platform/dashboard' : '/msp/dashboard')}
      />
    );
  }
  return children;
}

function ClientPage({ children }) {
  const location = useLocation();
  return <ClientRouteGuard><ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary></ClientRouteGuard>;
}

function RedirectAttackChainDetail() {
  const { pathname } = useLocation();
  const id = pathname.slice('/client/attack-chains/'.length);
  return <Navigate to={`/client/attack-paths/${encodeURIComponent(decodeURIComponent(id))}`} replace />;
}

function RequireAuth({ children }) {
  const { isAuthenticated, authReady } = useAuth();
  // Wait for silent refresh attempt before redirecting
  if (!authReady) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function RoleBasedRedirect() {
  const { isPlatformAdmin, isMspUser, isClientUser, profile } = useUser();
  const { clientId, setClientId } = useClient();

  // Set clientId before redirect so client dashboard has it on first render
  useEffect(() => {
    if (isClientUser === true && profile?.clientId && !clientId) {
      setClientId(profile.clientId);
    }
  }, [isClientUser, profile, clientId]);

  // Strict priority order — only exact true matches route to privileged dashboards
  if (isPlatformAdmin === true) return <Navigate to="/platform/dashboard" replace />;
  if (isMspUser === true) return <Navigate to="/msp/dashboard" replace />;
  if (isClientUser === true) {
    // Ensure clientId is set in context before navigating
    if (profile?.clientId && !clientId) {
      setClientId(profile.clientId);
    }
    return <Navigate to="/client/dashboard" replace />;
  }

  // Fallback — never default to platform or msp dashboard
  return <Navigate to="/client/dashboard" replace />;
}

function PlatformGuard({ children }) {
  const { isPlatformAdmin } = useUser();
  if (isPlatformAdmin !== true) return <Navigate to="/msp/dashboard" replace />;
  return children;
}

function MspGuard({ children }) {
  const { isPlatformAdmin, isMspUser } = useUser();
  if (isPlatformAdmin !== true && isMspUser !== true) return <Navigate to="/client/dashboard" replace />;
  return children;
}


export async function applyImpersonatedBranding(mspId, updateBranding, fetcher = fetchBranding) {
  if (!mspId || typeof updateBranding !== 'function') return false;

  clearBrandingCache();
  const branding = await fetcher({ mspId });
  updateBranding(branding);
  return true;
}

function ImpersonationBrandingSync() {
  const { impersonatedMsp } = useImpersonation();
  const updateBranding = useUpdateBranding();
  const lastAppliedMspId = useRef(null);
  const mspId = impersonatedMsp?.mspId ?? null;

  useEffect(() => {
    if (!mspId) {
      lastAppliedMspId.current = null;
      return undefined;
    }
    if (lastAppliedMspId.current === mspId) return undefined;

    lastAppliedMspId.current = mspId;
    let cancelled = false;
    applyImpersonatedBranding(
      mspId,
      branding => {
        if (!cancelled) updateBranding(branding);
      }
    ).catch(err => {
      if (cancelled) return;
      lastAppliedMspId.current = null;
      console.error('[Branding] Impersonation restore failed:', err);
    });

    return () => {
      cancelled = true;
      if (lastAppliedMspId.current === mspId) lastAppliedMspId.current = null;
    };
  }, [mspId, updateBranding]);

  return null;
}

function ImpersonationBanner() {
  const { impersonatedMsp, stopImpersonation } = useImpersonation();
  const { clientName } = useClient();
  const updateBranding = useUpdateBranding();
  const navigate = useNavigate();

  if (!impersonatedMsp) return null;

  const handleExit = async () => {
    stopImpersonation();
    clearBrandingCache();
    const branding = await fetchBranding(); // domain-based SSS branding
    updateBranding(branding);
    navigate('/platform/dashboard');
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
      background: 'linear-gradient(90deg, #f59e0b, #ef4444)',
      padding: '6px 20px',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
    }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', fontFamily: FONTS.sans }}>
        MSP view: {impersonatedMsp.mspName}{clientName ? ` managing ${clientName}` : ''}
      </span>
      <button
        onClick={handleExit}
        style={{
          background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)',
          borderRadius: 4, padding: '2px 10px', color: '#fff', fontSize: 10,
          fontWeight: 700, fontFamily: FONTS.mono, cursor: 'pointer',
        }}
      >
        Exit
      </button>
    </div>
  );
}

function ImpersonationOffset() {
  const { impersonatedMsp } = useImpersonation();
  if (!impersonatedMsp) return null;
  return <div style={{ height: 30 }} />;
}

function RoutePerformanceProbe() {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname;
    const startedAt = performance.now();
    let settled = false;
    let settleTimer = null;
    let maxTimer = null;
    let frame = null;

    const elapsed = () => Math.round(performance.now() - startedAt);
    const markSettled = (reason) => {
      if (settled) return;
      settled = true;
      if (settleTimer) clearTimeout(settleTimer);
      if (maxTimer) clearTimeout(maxTimer);
      markRouteTiming(pathname, 'settled', { elapsedMs: elapsed(), reason });
    };
    const scheduleSettleCheck = () => {
      if (settled) return;
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        if (currentApiInflight() === 0) {
          markSettled('api-idle');
        }
      }, 500);
    };

    markRouteTiming(pathname, 'start');
    frame = requestAnimationFrame(() => {
      markRouteTiming(pathname, 'first-frame', { elapsedMs: elapsed() });
      scheduleSettleCheck();
    });
    maxTimer = setTimeout(() => markSettled('max-window'), 4000);

    const onApiTiming = () => scheduleSettleCheck();
    window.addEventListener('sentinel:apiTiming', onApiTiming);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      if (settleTimer) clearTimeout(settleTimer);
      if (maxTimer) clearTimeout(maxTimer);
      window.removeEventListener('sentinel:apiTiming', onApiTiming);
    };
  }, [location.pathname]);

  return null;
}

// Auto-exit impersonation when user navigates FROM /msp/* TO /platform/*
function PlatformImpersonationGuard() {
  const location = useLocation();
  const { impersonatedMsp, stopImpersonation } = useImpersonation();
  const updateBranding = useUpdateBranding();
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    const prev = prevPath.current;
    prevPath.current = location.pathname;

    // Only fire on genuine impersonation exit: was on non-platform route, now on platform route
    if (!impersonatedMsp) return;
    if (!location.pathname.startsWith('/platform')) return;
    if (prev.startsWith('/platform')) return; // already on platform — not a transition

    stopImpersonation();
    clearBrandingCache();
    fetchBranding().then(data => updateBranding(data))
      .catch(err => console.error('[Branding] Platform restore failed:', err));
  }, [location.pathname]);

  return null;
}

function AuthenticatedLayout() {
  useTheme();

  return (
    <UserProvider>
    <ViewModeProvider>
    <ImpersonationProvider>
    <ImpersonationBrandingSync />
    <PlatformImpersonationGuard />
    <RoutePerformanceProbe />
    <FindingsProvider>
    <ImpersonationBanner />
    <ImpersonationOffset />
    <div style={{ display: 'flex', background: 'var(--color-background, ' + C.bg + ')', minHeight: '100vh', fontFamily: FONTS.sans, color: 'var(--color-text, ' + C.t1 + ')' }}>
      <Sidebar />
      <Routes>
        <Route path="/" element={<RoleBasedRedirect />} />

        {/* Platform routes */}
        <Route path="/platform/dashboard" element={<PlatformGuard><ErrorBoundary><PlatformDashboard /></ErrorBoundary></PlatformGuard>} />
        <Route path="/platform/msps/:mspId" element={<PlatformGuard><ErrorBoundary><PlatformMspDetail /></ErrorBoundary></PlatformGuard>} />
        <Route path="/platform/verify" element={<PlatformGuard><ErrorBoundary><WhiteLabelVerify /></ErrorBoundary></PlatformGuard>} />
        <Route path="/platform/settings" element={<PlatformGuard><ErrorBoundary><PlatformSettings /></ErrorBoundary></PlatformGuard>} />

        {/* Client routes */}
        <Route path="/client/dashboard" element={<ClientPage><ClientDashboard /></ClientPage>} />
        <Route path="/client/vulnerabilities" element={<Navigate to="/client/findings" replace />} />
        <Route path="/client/findings" element={<ClientPage><FindingsList key="findings" /></ClientPage>} />
        <Route path="/client/all-findings" element={<ClientPage><FindingsList key="all" all /></ClientPage>} />
        <Route path="/client/findings/:id" element={<ClientPage><FindingDetail /></ClientPage>} />
        <Route path="/client/assets" element={<ClientPage><Assets /></ClientPage>} />
        <Route path="/client/assets/:id" element={<ClientPage><AssetDetail /></ClientPage>} />
        <Route path="/client/osint" element={<LazyClientPage label="Loading OSINT"><Osint /></LazyClientPage>} />
        <Route path="/client/sensitive-data" element={<LazyClientPage label="Loading sensitive data"><SensitiveData /></LazyClientPage>} />
        <Route path="/client/compliance" element={<LazyClientPage label="Loading compliance"><Compliance /></LazyClientPage>} />
        <Route path="/client/compliance/evidence" element={<LazyClientPage label="Loading evidence"><ComplianceEvidence /></LazyClientPage>} />
        <Route path="/client/attack-paths" element={<LazyClientPage label="Loading attack paths"><AttackPaths /></LazyClientPage>} />
        <Route path="/client/attack-paths/:id" element={<LazyClientPage label="Loading attack path"><AttackPathDetail /></LazyClientPage>} />
        <Route path="/client/attack-chains" element={<Navigate to="/client/attack-paths" replace />} />
        <Route path="/client/attack-chains/:id" element={<RedirectAttackChainDetail />} />
        <Route path="/client/cloud-security" element={<LazyClientPage label="Loading cloud security"><CloudSecurity /></LazyClientPage>} />
        <Route path="/client/dast" element={<LazyClientPage label="Loading DAST"><DastScanning /></LazyClientPage>} />
        <Route path="/client/ad-security" element={<LazyClientPage label="Loading AD security"><ADSecurity /></LazyClientPage>} />
        <Route path="/client/scans" element={<ClientPage><ScanHistory /></ClientPage>} />
        <Route path="/client/scan-schedule" element={<ClientPage><ScanSchedule /></ClientPage>} />
        <Route path="/client/connections" element={<Navigate to="/client/credentials" replace />} />
        <Route path="/client/secrets" element={<ClientPage><ClientSecrets /></ClientPage>} />
        <Route path="/client/credentials" element={<ClientPage><ClientCredentials /></ClientPage>} />
        <Route path="/client/reports" element={<LazyClientPage label="Loading reports"><Reports /></LazyClientPage>} />

        {/* MSP routes — guarded so client-only users cannot access */}
        <Route path="/msp/dashboard" element={<MspGuard><ErrorBoundary><MspDashboard /></ErrorBoundary></MspGuard>} />
        <Route path="/msp/clients" element={<MspGuard><ErrorBoundary><MspClients /></ErrorBoundary></MspGuard>} />
        <Route path="/msp/alerts" element={<MspGuard><ErrorBoundary><MspAlerts /></ErrorBoundary></MspGuard>} />
        <Route path="/msp/reports" element={<MspGuard><ErrorBoundary><MspReports /></ErrorBoundary></MspGuard>} />
        <Route path="/msp/onboard-client" element={<Navigate to="/msp/onboard" replace />} />
        <Route path="/msp/onboard" element={<MspGuard><ErrorBoundary><OnboardingWizard /></ErrorBoundary></MspGuard>} />
        <Route path="/msp/team" element={<MspGuard><ErrorBoundary><MspTeam /></ErrorBoundary></MspGuard>} />
        <Route path="/msp/settings" element={<MspGuard><ErrorBoundary><MspSettings /></ErrorBoundary></MspGuard>} />

        {/* Account routes */}
        <Route path="/account/profile" element={<ErrorBoundary><Profile /></ErrorBoundary>} />
        <Route path="/account/settings" element={<ErrorBoundary><AccountSettings /></ErrorBoundary>} />
        <Route path="/account/alerts" element={<ErrorBoundary><AlertPreferences /></ErrorBoundary>} />
        <Route path="/account/team" element={<ErrorBoundary><TeamMembers /></ErrorBoundary>} />
        <Route path="/account/api-keys" element={<MspGuard><ErrorBoundary><ApiKeys /></ErrorBoundary></MspGuard>} />
        <Route path="/account/audit-log" element={<ErrorBoundary><AuditLog /></ErrorBoundary>} />
      </Routes>
    </div>
    </FindingsProvider>
    </ImpersonationProvider>
    </ViewModeProvider>
    </UserProvider>
  );
}

export default function App() {
  return (
    <ToastProvider>
    <AlertProvider>
      <AlertBanner />
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/auth/sso/callback" element={<SsoCallback />} />

          {/* Legacy redirect */}
          <Route path="/signin" element={<Navigate to="/login" replace />} />

          {/* Protected routes */}
          <Route path="/mfa-setup" element={<RequireAuth><MfaSetup /></RequireAuth>} />
          <Route path="/*" element={<RequireAuth><AuthenticatedLayout /></RequireAuth>} />
        </Routes>
      </BrowserRouter>
    </AlertProvider>
    </ToastProvider>
  );
}
